import type { z } from "zod";

import { type EdenStubForceSchema, getEnv, getEnvPublic, isCIEnvironment } from "./variables";

export type EdenStubForce = z.infer<typeof EdenStubForceSchema>;

const getEnvironment = () => {
  const nextEnv = getEnvPublic("NEXT_PUBLIC_VERCEL_ENV");
  if (nextEnv) {
    return nextEnv;
  }
  if (typeof window === "undefined") {
    const vercelEnv = getEnv("VERCEL_ENV");
    if (vercelEnv) {
      return vercelEnv;
    }
    const dopplerEnv = getEnv("DOPPLER_CONFIG");
    if (dopplerEnv) {
      return dopplerEnv;
    }
    return getEnv("NODE_ENV");
  }
  return "development";
};

/**
 * Returns true only if you are in the current production deployment for our apps
 *
 * For vercel apps, this is only true for the production deployment, not preview deployments
 * For local development, this is only true if NODE_ENV is set to production explicitly
 */
export function isProductionEnvironment(): boolean {
  return getEnvironment() === "production";
}

/**
 * Returns the deployment environment name as a string (e.g. "production",
 * "preview", "development", "test"). Empty string if indeterminate. Use for
 * tagging observability/telemetry payloads where downstream consumers need
 * to distinguish dev/preview/prod traffic.
 */
export function getDeployEnvironment(): string {
  return getEnvironment();
}

/**
 * Returns true only if you are in a local development environment
 */
export function isLocalDevelopmentEnvironment(): boolean {
  return getEnvironment() === "development";
}

/**
 * Server-side PostHog flags forced on for local development only, as a comma-separated list
 * (`LOCAL_FEATURE_FLAG_OVERRIDES=recruiting-crm pnpm dev`). Flags that only exist in the
 * production PostHog project are otherwise unreachable locally.
 */
export function isFeatureFlagOverriddenLocally(featureFlag: string): boolean {
  if (!isLocalDevelopmentEnvironment()) {
    return false;
  }
  const overrides = (getEnv("LOCAL_FEATURE_FLAG_OVERRIDES") ?? "").split(",").map((flag) => flag.trim());
  return overrides.includes(featureFlag);
}

/** Custom Vercel environments report VERCEL_ENV=preview, so the sandbox deploy sets this flag explicitly. */
export function isTrainingSandbox(): boolean {
  return getEnvPublic("NEXT_PUBLIC_TRAINING_SANDBOX") === "true" && !isProductionEnvironment();
}

/**
 * EagleView's sandbox is a canned-response stub that holds none of our claims, so every non-production
 * environment serves a committed fixture instead of calling it.
 */
export function isEagleViewReportMockEnabled(): boolean {
  return !isProductionEnvironment();
}

const isEdenProductionSignalSet = (): boolean =>
  isProductionEnvironment() || getEnv("VERCEL_ENV") === "production" || getEnv("DOPPLER_CONFIG") === "production";

export function isEdenSuggestionStubsEnabled(): boolean {
  return !isEdenProductionSignalSet() && getEnv("EDEN_SUGGESTION_STUBS") === "enabled";
}

export function isEdenAgentActionSuggestionsEnvEnabled(): boolean {
  return !isEdenProductionSignalSet() && getEnv("EDEN_AGENT_ACTION_SUGGESTIONS") === "enabled";
}

export function isEdenProductionEnvironment(): boolean {
  return isEdenProductionSignalSet();
}

export function isEdenLocalDevelopment(): boolean {
  return isLocalDevelopmentEnvironment() && !isEdenProductionSignalSet();
}

export function getEdenStubForce(): EdenStubForce | null {
  return isEdenProductionSignalSet() ? null : (getEnv("EDEN_STUB_FORCE") ?? null);
}

/** Serves a committed fixture outside production; WORKSITE_VIEW_SUMMARY_REAL_GENERATION_ENABLED="true"
 * spends real money. */
export function isWorksiteViewSummaryMockEnabled(): boolean {
  if (isProductionEnvironment()) {
    return false;
  }
  if (typeof window !== "undefined") {
    return true;
  }
  return getEnv("WORKSITE_VIEW_SUMMARY_REAL_GENERATION_ENABLED") !== "true";
}

/** Serves a committed fixture outside production; SITE_DISCOVERY_SUMMARY_REAL_GENERATION_ENABLED="true"
 * spends real money. */
export function isSiteDiscoverySummaryMockEnabled(): boolean {
  if (isProductionEnvironment()) {
    return false;
  }
  if (typeof window !== "undefined") {
    return true;
  }
  return getEnv("SITE_DISCOVERY_SUMMARY_REAL_GENERATION_ENABLED") !== "true";
}

/** The token table only ever holds production company ids, so no non-production environment can
 * reach QuickBooks at all. */
export function isQuickbooksMockEnabled(): boolean {
  return !isProductionEnvironment();
}

/** The CI E2E server runs `next start`, which reads as production, so CI and test runs are excluded
 * explicitly: only real production files Linear issues. */
export function isLinearIssueFilingEnabled(): boolean {
  return isProductionEnvironment() && !isTestEnvironment() && !isCIEnvironment();
}

/**
 * Returns true if in a test or E2E environment (vitest, NODE_ENV=test, or E2E=true)
 */
export function isTestEnvironment(): boolean {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("VITEST") === "true" || getEnv("NODE_ENV") === "test" || getEnv("E2E") === "true";
}

/**
 * Returns true if hubspot deals are enabled
 *
 * @returns true
 */
export function isHubspotDealsEnabled() {
  if (isProductionEnvironment()) {
    return true;
  }
  return false;
}

/** True only in production with both Conversions API variables set; a production instance missing either reads as disabled. */
export function isMetaCapiEnabled(): boolean {
  return isProductionEnvironment() && !!getEnv("DOMBOL_META_CONVERSIONS_API_KEY") && !!getEnv("DOMBOL_META_DATASET_ID");
}

export function isFingerprintEnabled(): boolean {
  return !!getEnv("FINGERPRINT_SECRET_API_KEY") && getEnv("MARKETING_SITE_ORIGINS").length > 0;
}

/**
 * True only when all three leadgen variables are set, in every environment (preview exercises the route with
 * fixtures). The Instant Forms webhook route answers 503 and the Graph lead fetch throws META_LEADGEN_NOT_CONFIGURED
 * while this is false; the Conversions API send path never reads it.
 */
export function isMetaLeadgenEnabled(): boolean {
  return (
    !!getEnv("DOMBOL_META_APP_SECRET") &&
    !!getEnv("DOMBOL_META_PAGE_ACCESS_TOKEN") &&
    !!getEnv("DOMBOL_META_WEBHOOK_VERIFY_TOKEN")
  );
}

/** `GOOGLE_ADS_LOGIN_CUSTOMER_ID` is left out: it is only needed when the LSA account is reached through a manager account. */
export function isGoogleLsaIntakeEnabled(): boolean {
  return (
    !!getEnv("GOOGLE_ADS_SERVICE_ACCOUNT_CLIENT_EMAIL") &&
    !!getEnv("GOOGLE_ADS_SERVICE_ACCOUNT_PRIVATE_KEY") &&
    !!getEnv("GOOGLE_ADS_LSA_CUSTOMER_ID")
  );
}

/**
 * Temporary pause for Atlas NTP (Notice to Proceed) follow-up tickets. The gated NOTICE_TO_PROCEED SLA
 * check only fires for upsell bids (salesOutReachEval returns false otherwise), and those route to the
 * sales teams, which track this follow-up in HubSpot, so the tickets are redundant noise. Remove this
 * flag and the guard in ai/atlas/data/sla/sla-mapping.ts once CRM is brought in-house.
 */
export function isAtlasNtpOutreachPaused() {
  return true;
}

export function isWorkOrderLockEnforcementEnabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WORK_ORDER_LOCK_ENFORCEMENT_ENABLED") === "true";
}

export function isWesthillProcessingDisabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_PROCESSING_DISABLED");
}

export function isWesthillStatusUpdatesEnabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_STATUS_UPDATES_ENABLED");
}

export function isWesthillXaFileUploadEnabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_XA_FILE_UPLOAD_ENABLED");
}

export function isWesthillXaDocumentCheckDisabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_XA_DOCUMENT_CHECK_DISABLED");
}

export function isWesthillXaAutomationsDisabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_XA_AUTOMATIONS_DISABLED");
}

export function isWesthillXaNoteEmailIngestEnabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return getEnv("WESTHILL_XA_NOTE_EMAIL_INGEST_ENABLED");
}

export function isWesthillJobLevelTargetDatesEnabled() {
  if (typeof window !== "undefined") {
    return false;
  }
  return !getEnv("WESTHILL_JOB_LEVEL_TARGET_DATES_DISABLED");
}

export function getWesthillXaTabCount(): number {
  return getEnv("WESTHILL_XA_TAB_COUNT");
}

/**
 * Returns the git commit SHA of the running deployment, checking each platform's
 * env var (Vercel, Flightcontrol, then a generic override). Null when none is
 * present (local dev) — callers must treat the SHA as best-effort provenance.
 */
export function getCommitSha(): string | null {
  return getEnv("VERCEL_GIT_COMMIT_SHA") ?? getEnv("FC_GIT_COMMIT_SHA") ?? getEnv("GIT_COMMIT_SHA") ?? null;
}

export * from "./variables";
